/**
 * Frontend locale registry — mirrors backend `services/locale_config.py`.
 *
 * Single source of truth on the client for currency symbol/position, decimals,
 * timezone, date/time format, tax label/rate, and messaging channel — all keyed
 * by the operator's `organizations.country` (ISO-3166-1 alpha-2, with aliases).
 *
 * DO NOT scatter "$" / "AED" / "Asia/Dubai" / "5%" literals around the app.
 * If you need a new country, extend LOCALES here (and the backend registry).
 */

export const LOCALES = {
  US: {
    countryCode: 'US',
    countryName: 'United States',
    currencyCode: 'USD',
    currencySymbol: '$',
    currencyPosition: 'prefix',
    decimals: 2,
    defaultTimezone: 'America/Chicago',
    dateFormat: 'MM/DD/YYYY',
    use24Hour: false,
    taxLabel: 'Sales Tax',
    taxDefaultRate: 0, // varies by state — operator sets it
    taxInclusive: false,
    preferredChannel: 'sms',
    phonePrefix: '+1',
    locale: 'en-US',
  },
  AE: {
    countryCode: 'AE',
    countryName: 'United Arab Emirates',
    currencyCode: 'AED',
    currencySymbol: 'AED',
    currencyPosition: 'prefix',
    decimals: 2,
    defaultTimezone: 'Asia/Dubai',
    dateFormat: 'DD/MM/YYYY',
    longDateFormat: 'EEE, DD MMM YYYY', // "Wed, 24 Jun 2026"
    use24Hour: false,                   // product decision: keep 12-hour "2:00 PM GST"
    taxLabel: 'VAT',
    taxDefaultRate: 5,
    taxInclusive: true,
    preferredChannel: 'whatsapp',
    phonePrefix: '+971',
    locale: 'en-AE',
  },
  GB: {
    countryCode: 'GB',
    countryName: 'United Kingdom',
    currencyCode: 'GBP',
    currencySymbol: '£',
    currencyPosition: 'prefix',
    decimals: 2,
    defaultTimezone: 'Europe/London',
    dateFormat: 'DD/MM/YYYY',
    use24Hour: true,
    taxLabel: 'VAT',
    taxDefaultRate: 20,
    taxInclusive: true,
    preferredChannel: 'sms',
    phonePrefix: '+44',
    locale: 'en-GB',
  },
  CA: {
    countryCode: 'CA',
    countryName: 'Canada',
    currencyCode: 'CAD',
    currencySymbol: '$',
    currencyPosition: 'prefix',
    decimals: 2,
    defaultTimezone: 'America/Toronto',
    dateFormat: 'YYYY-MM-DD',
    use24Hour: true,
    taxLabel: 'GST/HST',
    taxDefaultRate: 5,
    taxInclusive: false,
    preferredChannel: 'sms',
    phonePrefix: '+1',
    locale: 'en-CA',
  },
};

// Historical / user-supplied aliases coerced to the canonical 2-letter code.
const ALIASES = {
  USA: 'US', US: 'US', 'U.S.': 'US',
  UAE: 'AE', AE: 'AE', 'U.A.E.': 'AE',
  UK: 'GB', GB: 'GB',
  CAN: 'CA', CA: 'CA',
};

export const DEFAULT_COUNTRY = 'US';

/** Resolve any country identifier (e.g. "USA", "uae") to a registered code. */
export function normalizeCountryCode(code) {
  if (!code) return DEFAULT_COUNTRY;
  const key = String(code).trim().toUpperCase();
  if (LOCALES[key]) return key;
  return ALIASES[key] || DEFAULT_COUNTRY;
}

/** Return the locale descriptor for a country code. Never throws. */
export function localeForCountry(code) {
  return LOCALES[normalizeCountryCode(code)];
}

/**
 * Format a money amount per a locale. Mirrors the backend `format_money`:
 * multi-character symbols ("AED") get a trailing space, single-char ("$","£")
 * do not; suffix-position locales render "1,234.50 <symbol>".
 */
export function formatMoneyForLocale(amount, loc) {
  const safe = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  const rendered = Math.abs(safe).toLocaleString(loc.locale || 'en-US', {
    minimumFractionDigits: loc.decimals,
    maximumFractionDigits: loc.decimals,
  });
  const sign = safe < 0 ? '-' : '';
  if (loc.currencyPosition === 'suffix') {
    return `${sign}${rendered} ${loc.currencySymbol}`;
  }
  const sep = loc.currencySymbol.length > 1 ? ' ' : '';
  return `${sign}${loc.currencySymbol}${sep}${rendered}`;
}

// Fixed timezone abbreviations for display (UAE 'GST' has no DST).
const TZ_ABBR = {
  'America/New_York': 'EST', 'America/Chicago': 'CST', 'America/Denver': 'MST',
  'America/Phoenix': 'MST', 'America/Los_Angeles': 'PST', 'America/Anchorage': 'AKST',
  'Pacific/Honolulu': 'HST', 'America/Toronto': 'EST', 'America/Vancouver': 'PST',
  'Asia/Dubai': 'GST', 'Europe/London': 'GMT',
};

/** Short display abbreviation for a timezone (e.g. "CST", "GST"). */
export function tzAbbr(tzName) {
  if (!tzName) return '';
  return TZ_ABBR[tzName] || tzName.split('/').pop().replace(/_/g, ' ');
}

/**
 * Explicit long date per locale — no DD/MM ambiguity.
 *   US/CA → "June 24, 2026"
 *   AE/GB → "Wed, 24 Jun 2026"
 */
export function formatLongDateForLocale(date, loc) {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const cc = loc.countryCode;
  if (cc === 'US' || cc === 'CA') {
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}
